import { Plus, Trash2 } from 'lucide-react';
import { Input } from 'src/components/ui/input';
import { cn } from 'src/lib/utils';
import type { BrandBinding, SceneGraph, SceneLayer } from '../types/creative-template-example.types';
import type { CreativeRole } from '../types/creative-template.types';
import { BINDABLE_FIELD_OPTIONS } from '../types/creative-template-example.types';
import { CREATIVE_ROLE_OPTIONS } from '../types/creative-template.types';
import { createDefaultLayer } from '../helpers/creative-template-example.helpers';
import { SchedulerDsButton } from './scheduler-ds-button';

const labelClassName = 'text-sm font-medium text-gray-700';
const selectClassName = cn(
  'h-10 w-full appearance-none rounded border border-gray-400 bg-white pl-4 pr-10 text-base text-black',
  'focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-black',
);

interface BrandBindingsEditorProps {
  bindings: BrandBinding[];
  onChange: (bindings: BrandBinding[]) => void;
}

export function BrandBindingsEditor({ bindings, onChange }: BrandBindingsEditorProps) {
  const updateBinding = (index: number, next: BrandBinding) => {
    onChange(bindings.map((binding, itemIndex) => (itemIndex === index ? next : binding)));
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Brand bindings</h3>
        <SchedulerDsButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => onChange([...bindings, { role: 'logo', field: 'media_url', token: 'brand.logo' }])}
        >
          <Plus className="size-4" />
          Add binding
        </SchedulerDsButton>
      </div>

      {bindings.map((binding, index) => (
        <div key={`binding-${index}`} className="grid gap-3 rounded-md border border-gray-300 bg-white p-3 md:grid-cols-3">
          <label className="relative block space-y-1">
            <span className={labelClassName}>Role</span>
            <select
              value={binding.role}
              onChange={(event) => updateBinding(index, { ...binding, role: event.target.value as CreativeRole })}
              className={selectClassName}
            >
              {CREATIVE_ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </label>
          <label className="relative block space-y-1">
            <span className={labelClassName}>Field</span>
            <select
              value={binding.field}
              onChange={(event) => updateBinding(index, { ...binding, field: event.target.value as BrandBinding['field'] })}
              className={selectClassName}
            >
              {BINDABLE_FIELD_OPTIONS.map((field) => (
                <option key={field} value={field}>{field}</option>
              ))}
            </select>
          </label>
          <label className="block space-y-1">
            <span className={labelClassName}>Token</span>
            <Input
              value={binding.token}
              onChange={(event) => updateBinding(index, { ...binding, token: event.target.value })}
              placeholder="brand.palette.primary"
            />
          </label>
          <div className="md:col-span-3">
            <SchedulerDsButton
              type="button"
              variant="danger-ghost"
              size="sm"
              onClick={() => onChange(bindings.filter((_, itemIndex) => itemIndex !== index))}
            >
              <Trash2 className="size-4" />
              Remove binding
            </SchedulerDsButton>
          </div>
        </div>
      ))}
    </section>
  );
}

interface SceneGraphEditorProps {
  sceneGraph: SceneGraph;
  onChange: (sceneGraph: SceneGraph) => void;
}

function LayerContentFields({
  layer,
  onChange,
}: {
  layer: SceneLayer;
  onChange: (layer: SceneLayer) => void;
}) {
  const { content } = layer;

  if (content.kind === 'fill') {
    return (
      <label className="block space-y-1">
        <span className={labelClassName}>Fill colour</span>
        <Input
          value={content.fill_color}
          onChange={(event) => onChange({
            ...layer,
            content: { ...content, fill_color: event.target.value },
          })}
        />
      </label>
    );
  }

  if (content.kind === 'image') {
    return (
      <>
        <label className="block space-y-1">
          <span className={labelClassName}>Media URL</span>
          <Input
            value={content.media_url ?? ''}
            onChange={(event) => onChange({
              ...layer,
              content: { ...content, media_url: event.target.value || null },
            })}
          />
        </label>
        <label className="block space-y-1">
          <span className={labelClassName}>Label</span>
          <Input
            value={content.label}
            onChange={(event) => onChange({
              ...layer,
              content: { ...content, label: event.target.value },
            })}
          />
        </label>
      </>
    );
  }

  if (content.kind === 'shape') {
    return (
      <label className="block space-y-1">
        <span className={labelClassName}>Fill colour</span>
        <Input
          value={content.fill_color}
          onChange={(event) => onChange({
            ...layer,
            content: { ...content, fill_color: event.target.value },
          })}
        />
      </label>
    );
  }

  return (
    <>
      <label className="block space-y-1 md:col-span-2">
        <span className={labelClassName}>Content</span>
        <Input
          value={content.content}
          onChange={(event) => onChange({
            ...layer,
            content: { ...content, content: event.target.value },
          })}
        />
      </label>
      <label className="block space-y-1">
        <span className={labelClassName}>Font family</span>
        <Input
          value={content.font_family}
          onChange={(event) => onChange({
            ...layer,
            content: { ...content, font_family: event.target.value },
          })}
        />
      </label>
      <label className="block space-y-1">
        <span className={labelClassName}>Text colour</span>
        <Input
          value={content.text_color}
          onChange={(event) => onChange({
            ...layer,
            content: { ...content, text_color: event.target.value },
          })}
        />
      </label>
      <label className="block space-y-1">
        <span className={labelClassName}>Font size</span>
        <Input
          type="number"
          value={content.font_size ?? ''}
          onChange={(event) => onChange({
            ...layer,
            content: {
              ...content,
              font_size: event.target.value === '' ? null : Number(event.target.value),
            },
          })}
        />
      </label>
    </>
  );
}

export function SceneGraphEditor({ sceneGraph, onChange }: SceneGraphEditorProps) {
  const updateLayer = (index: number, next: SceneLayer) => {
    onChange({
      ...sceneGraph,
      layers: sceneGraph.layers.map((layer, itemIndex) => (itemIndex === index ? next : layer)),
    });
  };

  return (
    <section className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <h3 className="text-sm font-medium uppercase tracking-wide text-gray-500">Scene graph layers</h3>
        <SchedulerDsButton
          type="button"
          variant="secondary"
          size="sm"
          onClick={() => {
            const role = window.prompt('Role name:')?.trim() as CreativeRole | undefined;
            if (role && CREATIVE_ROLE_OPTIONS.includes(role)) {
              onChange({
                ...sceneGraph,
                layers: [...sceneGraph.layers, createDefaultLayer(role)],
              });
            }
          }}
        >
          <Plus className="size-4" />
          Add layer
        </SchedulerDsButton>
      </div>

      {sceneGraph.layers.map((layer, index) => (
        <div key={`layer-${index}`} className="space-y-3 rounded-md border border-gray-300 bg-gray-50 p-4">
          <div className="text-sm font-medium text-black">{layer.role}</div>
          <div className="grid gap-3 md:grid-cols-4">
            {(['x', 'y', 'w', 'h'] as const).map((field) => (
              <label key={field} className="block space-y-1">
                <span className={labelClassName}>Zone {field}</span>
                <Input
                  type="number"
                  step="0.01"
                  value={layer.zone[field]}
                  onChange={(event) => updateLayer(index, {
                    ...layer,
                    zone: { ...layer.zone, [field]: Number(event.target.value) },
                  })}
                />
              </label>
            ))}
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <LayerContentFields layer={layer} onChange={(next) => updateLayer(index, next)} />
          </div>
          <SchedulerDsButton
            type="button"
            variant="danger-ghost"
            size="sm"
            onClick={() => onChange({
              ...sceneGraph,
              layers: sceneGraph.layers.filter((_, itemIndex) => itemIndex !== index),
            })}
          >
            <Trash2 className="size-4" />
            Remove layer
          </SchedulerDsButton>
        </div>
      ))}
    </section>
  );
}
